/* eslint-disable react-hooks/exhaustive-deps */
import {
  useMediaQuery,
  Grid,
  Box,
  useTheme,
  Typography,
  Button,
  Container,
  Skeleton,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  SelectChangeEvent,
  MenuItem,
  TextField,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import CustomRightImage from "@components/CustomRightImagev2";
import { useLazyGetAmountsCrediviveQuery, useLazyGetTaxSystemConfigQuery } from "@api/applications";
import { useEffect, useState } from "react";
import { PayrollAgreementsStructure, TaxSystemConfigResponse } from "@interfaces/index";
import { ApiError } from "@interfaces/response";
import { amountToText, formatCurrency } from "@helpers/funciones";
import { useAppDispatch } from "@store/store";
import { setModalErrorVisible } from "@store/slices/appSlice";
import * as Sentry from "@sentry/react";
import { StyledSlider } from "@components/CustomSlider";
import useInterfaceConfig from "@hooks/useInterfaceConfig";
import { useLazyGetPayrollAgreementsCatalogQuery } from "@api/catalogs";

const TAX_SYSTEM_CODE = "CVNPF";

const CotizadorCredivive = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const interfaceConfig = useInterfaceConfig();
  const dispatch = useAppDispatch();

  const [getTaxSystemConfig] = useLazyGetTaxSystemConfigQuery();
  const [getAmountsCredivive] = useLazyGetAmountsCrediviveQuery();
  const [getPayrollAgreementsCatalog] = useLazyGetPayrollAgreementsCatalogQuery();

  const [configConvenio, setConfigConvenio] = useState<TaxSystemConfigResponse | null>(null);
  const [payrollAgreements, setPayrollAgreements] = useState<PayrollAgreementsStructure[]>([]);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isCalculating, setIsCalculating] = useState<boolean>(false);
  const [amountMarks, setAmountMarks] = useState<{ value: number; label: string }[]>([]);
  const [termMarks, setTermMarks] = useState<{ value: number; label: string }[]>([]);
  const [cotizadorConfig, setCotizadorConfig] = useState<TaxSystemConfigResponse>({
    id: "",
    code: "",
    product_id: "",
    minAmount: 0,
    maxAmount: 0,
    stepAmount: 0,
    minTerm: 0,
    maxTerm: 0,
    stepTerm: 0,
    defaultAmount: 0,
    defaultTerm: 0,
    tax_system_type: "",
  });

  const [entityName, setEntityName] = useState<string>("");
  const [selectedAmount, setSelectedAmount] = useState<number>(cotizadorConfig.defaultAmount);
  const [selectedTerm, setSelectedTerm] = useState<number>(cotizadorConfig.defaultTerm);
  const [monthlyRate, setMonthlyRate] = useState<number>(0);
  const [company, setCompany] = useState<string>("");
  const [companyTouched, setCompanyTouched] = useState<boolean>(false);
  const [convenioId, setConvenioId] = useState<string>("");
  const [companyDescription, setCompanyDescription] = useState<string>("");
  const sourceCode = new URLSearchParams(location.search).get("code") || "";

  // La configuracion (tasa, plazo minimo/maximo) ya no se consulta por source_code;
  // ahora se toma directamente del convenio de la empresa seleccionada.
  const selectedAgreement = payrollAgreements.find((agreement) => agreement.display_name === company) || null;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);

        const payrollAgreementsData = await getPayrollAgreementsCatalog().unwrap();
        setPayrollAgreements(payrollAgreementsData);

        const responseConvenio = await getTaxSystemConfig(TAX_SYSTEM_CODE).unwrap();
        setConfigConvenio(responseConvenio);
      } catch (error) {
        console.error("Error fetching tax system configs", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [getTaxSystemConfig]);

  useEffect(() => {
    const { minAmount, maxAmount, stepAmount, minTerm, maxTerm, stepTerm, defaultAmount, defaultTerm } =
      cotizadorConfig;

    if (minAmount < maxAmount && stepAmount > 0) {
      const marks = [];

      marks.push({ value: minAmount, label: amountToText(minAmount) });
      marks.push({ value: maxAmount, label: amountToText(maxAmount) });

      setAmountMarks(marks);
    }

    if (minTerm < maxTerm && stepTerm > 0) {
      const marks = [];

      for (let i = minTerm; i <= maxTerm; i += stepTerm) {
        marks.push({ value: i, label: `${i}` });
      }

      setTermMarks(marks);
      setSelectedAmount(defaultAmount);
      setSelectedTerm(defaultTerm);
    }
  }, [cotizadorConfig]);

  // La seleccion de empresa ahora es obligatoria: al elegir una empresa se usa la tasa
  // y el plazo del convenio de esa empresa (catalogo de payroll_agreements); sin una
  // empresa seleccionada no hay configuracion (los montos/plazo permanecen ocultos).
  useEffect(() => {
    if (selectedAgreement) {
      setEntityName(selectedAgreement.display_name || "");
      setConvenioId(selectedAgreement.id);

      if (configConvenio) {
        const rawInterestRate = selectedAgreement.interest_rate ?? 0;
        // el catalogo puede traer la tasa como fracción (0.3); calculateAmountMonthly
        // espera formato porcentaje (30), así que se normaliza aquí.
        const interestRate = rawInterestRate > 0 && rawInterestRate <= 1 ? rawInterestRate * 100 : rawInterestRate;

        setCotizadorConfig({
          ...configConvenio,
          minTerm: selectedAgreement.min_term,
          maxTerm: selectedAgreement.max_term,
          stepTerm: selectedAgreement.jump_term ?? configConvenio.stepTerm,
          defaultTerm: selectedAgreement.min_term,
          interest_rate: interestRate,
        });
      }
      return;
    }

    setEntityName("");
    setConvenioId("");

    if (company) {
      // Empresa capturada manualmente (p.ej. "Otra") sin convenio propio: usa el
      // convenio general.
      if (configConvenio) setCotizadorConfig(configConvenio);
    }
  }, [company, selectedAgreement, configConvenio]);

  // El pago se consulta al servicio solo al presionar "Calcular pago mensual" para no disparar
  // una petición por cada movimiento del slider; al cambiar monto/plazo/empresa se limpia.
  useEffect(() => {
    setMonthlyRate(0);
  }, [selectedAmount, selectedTerm, selectedAgreement, cotizadorConfig.interest_rate, interfaceConfig?.interest_rate]);

  // Con convenio se usa su tasa; si el convenio no trae tasa (o viene en 0) o no hay convenio, la del theme.
  const interestRate = (selectedAgreement && cotizadorConfig.interest_rate) || interfaceConfig?.interest_rate || 0;

  const handleCalculateMonthlyPayment = async () => {
    if (!selectedAmount || !selectedTerm) {
      return;
    }

    try {
      setIsCalculating(true);
      const response = await getAmountsCredivive({
        monto: selectedAmount,
        plazo: selectedTerm,
        tasa: interestRate,
      }).unwrap();
      setMonthlyRate(parseFloat(response.CuotaFija) || 0);
    } catch (error) {
      console.log("Ha ocurrido un error al calcular los montos", error);
      Sentry.captureException(error, {
        extra: {
          component: "CotizadorCredivive",
        },
      });
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
    } finally {
      setIsCalculating(false);
    }
  };

  const setConditions = () => {
    if (!cotizadorConfig.product_id || !selectedAmount || !selectedAmount || !monthlyRate) {
      return;
    }
    const sourceCode = sessionStorage.getItem("source_code");

    let urlPortal = `/producto/creditosimple/crear_cuenta?tax_system_id=${cotizadorConfig.id}&product_id=${cotizadorConfig.product_id}&person_type=${cotizadorConfig.code}&loan_amount=${selectedAmount}&loan_term=${selectedTerm}&tax_system_type=${cotizadorConfig.tax_system_type}`;
    if (sourceCode) {
      urlPortal += `&source_code=${sourceCode}`;
    }
    if (convenioId) {
      urlPortal += `&convenio_id=${convenioId}`;
    }
    if (interestRate) {
      urlPortal += `&tasa=${interestRate}`;
    }
    navigate(urlPortal);
  };

  useEffect(() => {
    if (sourceCode) {
      sessionStorage.setItem("source_code", sourceCode);
    } else {
      sessionStorage.removeItem("source_code");
    }
  }, [sourceCode]);

  useEffect(() => {
    if (company !== "Otra") {
      setCompanyDescription("");
    }
  }, [company]);

  return (
    <>
      <Grid container height="100%">
        <Grid item xs={12} md={8}>
          <Container maxWidth={"sm"}>
            <Typography fontWeight={500} variant="h3" mt={4} color={theme?.palette?.primary?.dark} textAlign={"center"}>
              ¡HOLA!
              <br />
              {entityName && <span>{entityName}</span>}
            </Typography>

            <Box>
              <Typography
                fontWeight={500}
                fontSize={"16px"}
                mt={1}
                color={theme?.palette?.primary?.dark}
                textAlign={"center"}
              >
                Selecciona el monto a solicitar y el plazo deseado
              </Typography>

              <Box mt={1} ml={isMobileOrTablet ? 3 : 1} mr={isMobileOrTablet ? 3 : 1}>
                <FormControl
                  fullWidth
                  required
                  sx={{ mt: 2 }}
                  variant="outlined"
                  error={companyTouched && !company}
                >
                  <InputLabel id="company-label">Empresa donde laboras</InputLabel>
                  <Select
                    labelId="company-label"
                    id="company-select"
                    name="company"
                    fullWidth
                    required
                    value={company}
                    onChange={(e: SelectChangeEvent<string>) => {
                      setCompany(e.target.value as string);
                    }}
                    onBlur={() => setCompanyTouched(true)}
                    disabled={isLoading}
                    label="Empresa donde laboras"
                  >
                    <MenuItem value="">Selecciona la empresa</MenuItem>
                    {payrollAgreements.map((agreement) => (
                      <MenuItem key={agreement.id} value={agreement.display_name}>
                        {agreement.display_name}
                      </MenuItem>
                    ))}
                  </Select>
                  {companyTouched && !company && <FormHelperText>Selección requerida</FormHelperText>}
                </FormControl>

                {company === "Otra" && (
                  <FormControl fullWidth>
                    <TextField
                      id="company_description"
                      name="company_description"
                      label="Especifica la empresa"
                      margin="normal"
                      fullWidth
                      autoComplete="off"
                      onChange={(e) => {
                        setCompanyDescription(e.target.value as string);
                      }}
                      value={companyDescription}
                      inputProps={{ maxLength: 150 }}
                      disabled={isLoading}
                    />
                  </FormControl>
                )}
              </Box>

              {!company && (
                <Typography
                  fontWeight={500}
                  fontSize={"14px"}
                  mt={2}
                  color={theme?.palette?.primary?.dark}
                  textAlign={"center"}
                >
                  Selecciona una compañía para poder continuar
                </Typography>
              )}

              {company && (
                <>
                  <Box mt={1} ml={isMobileOrTablet ? 3 : 1} mr={isMobileOrTablet ? 3 : 1}>
                    <Typography
                      fontWeight={500}
                      fontSize={"16px"}
                      color={theme?.palette?.primary?.dark}
                      textAlign={"center"}
                    >
                      Selecciona el monto
                    </Typography>

                    <Typography
                      fontWeight={700}
                      fontSize={"13px"}
                      mt={1}
                      color={theme?.palette?.primary?.main}
                      textAlign={"center"}
                    >
                      {formatCurrency(selectedAmount)}
                    </Typography>

                    {!isLoading ? (
                      <StyledSlider
                        aria-label="amount"
                        min={cotizadorConfig.minAmount}
                        max={cotizadorConfig.maxAmount}
                        step={cotizadorConfig.stepAmount}
                        marks={amountMarks}
                        defaultValue={cotizadorConfig.defaultAmount}
                        value={selectedAmount}
                        onChange={(_event, newValue) => setSelectedAmount(newValue as number)}
                      />
                    ) : (
                      <Skeleton variant="rectangular" animation="wave" />
                    )}
                  </Box>

                  <Box mb={1} ml={isMobileOrTablet ? 3 : 1} mr={isMobileOrTablet ? 3 : 1}>
                    <Typography
                      fontWeight={500}
                      fontSize={"16px"}
                      mb={2}
                      color={theme?.palette?.primary?.dark}
                      textAlign={"center"}
                    >
                      Selecciona el plazo en meses
                    </Typography>

                    {!isLoading ? (
                      <StyledSlider
                        aria-label="term"
                        min={cotizadorConfig.minTerm}
                        max={cotizadorConfig.maxTerm}
                        step={cotizadorConfig.stepTerm}
                        defaultValue={cotizadorConfig.defaultTerm}
                        marks={termMarks}
                        value={selectedTerm}
                        onChange={(_event, newValue) => setSelectedTerm(newValue as number)}
                        valueLabelDisplay="on"
                      />
                    ) : (
                      <Skeleton variant="rectangular" animation="wave" />
                    )}
                  </Box>

                  <Box display="flex" justifyContent="center" mb={2}>
                    <Button
                      variant="outlined"
                      onClick={handleCalculateMonthlyPayment}
                      disabled={isLoading || isCalculating || !selectedAmount || !selectedTerm}
                    >
                      {isCalculating ? "Calculando..." : "Calcular pago mensual"}
                    </Button>
                  </Box>

                  <Typography
                    fontWeight={700}
                    fontSize={"16px"}
                    color={theme?.palette?.primary?.main}
                    textAlign={"center"}
                  >
                    Pago mensual estimado*
                  </Typography>

                  <Typography
                    mb={2}
                    textAlign={"center"}
                    variant="h4"
                    fontWeight={theme?.palette?.fontWeights?.semibold}
                    color={theme?.palette?.primary?.dark}
                  >
                    {isNaN(monthlyRate) || !isFinite(monthlyRate) ? "0" : formatCurrency(monthlyRate)}{" "}
                    <Typography
                      variant="h5"
                      component={"span"}
                      fontWeight={theme?.palette?.fontWeights?.semibold}
                      color={theme?.palette?.primary?.dark}
                    >
                      MXN
                    </Typography>
                  </Typography>

                  <Typography
                    textAlign={"center"}
                    fontWeight={theme?.palette?.fontWeights?.light}
                    color={theme?.palette?.primary?.dark}
                    mt={2}
                    mb={2}
                  >
                    <small>
                      *Esta simulación es para fines demostrativos y está sujeto a aprobación, las condiciones como la
                      tasa, el monto del crédito, o la comisión por apertura, podrían cambiar dependiendo el perfil y
                      la situación actual del solicitante.
                    </small>
                  </Typography>

                  <Box display="flex" justifyContent="center" mb={2}>
                    <Button variant="contained" onClick={setConditions} disabled={isLoading || isCalculating || !monthlyRate}>
                      Continuar
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          </Container>
        </Grid>
        {!isMobileOrTablet && (
          <Grid item xs={12} md={4}>
            <CustomRightImage />
          </Grid>
        )}
      </Grid>
    </>
  );
};
export default CotizadorCredivive;
